import { Component, lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import Toast from './components/Toast';
import { messaging, onMessage } from './firebase';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Schedules from './pages/Schedules';
import ScheduleBulkForm from './pages/ScheduleBulkForm';
import QuickReserve from './pages/QuickReserve';
import Reservations from './pages/Reservations';
import Logs from './pages/Logs';
import Pricing from './pages/Pricing';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminReserve from './pages/admin/AdminReserve';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSchedules from './pages/admin/AdminSchedules';
import AdminTaekyung from './pages/admin/AdminTaekyung';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminSettings from './pages/admin/AdminSettings';
import Referral from './pages/Referral';
import { COMMERCE_ENABLED, REFERRAL_ENABLED } from './config/features';

const RouteMap = lazy(() => import('./pages/RouteMap'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,          // 30초간 fresh 취급 (재호출 안 함)
      gcTime: 5 * 60_000,         // 5분간 캐시 유지
      retry: 1,                   // 실패 시 1회 재시도
      refetchOnWindowFocus: true, // 포커스 복귀 시 자동 갱신
    },
  },
});

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <h2 style={{ color: '#e53e3e', marginBottom: 8 }}>오류가 발생했습니다</h2>
          <pre style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error.message}
            {'\n'}
            {this.state.error.stack}
          </pre>
          <button onClick={() => { localStorage.clear(); location.reload(); }}
            style={{ marginTop: 16, padding: '8px 16px', background: '#2B7FFF', color: '#fff', border: 'none', borderRadius: 8 }}>
            초기화 후 새로고침
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ReferralRedirect() {
  const { isAuthenticated } = useAuthStore();
  const { code } = useParams();
  if (!REFERRAL_ENABLED) return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
  if (isAuthenticated) return <Navigate to={`/dashboard?ref=${code}`} replace />;
  return <Navigate to={`/login?ref=${code}`} replace />;
}

function AdminRoute() {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'admin') return <Navigate to="/dashboard" />;
  return <Outlet />;
}

function App() {
  const { isAuthenticated, _initFocusRefresh } = useAuthStore();

  // window focus 시 구독 상태 자동 갱신 등록
  useEffect(() => {
    _initFocusRefresh();
  }, []);

  // 글로벌 포그라운드 FCM 알림 리스너 (어떤 페이지에서든 알림 수신)
  useEffect(() => {
    if (!messaging) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      const { title, body } = payload.data || {};
      if (title && 'Notification' in window && window.Notification.permission === 'granted') {
        new window.Notification(title, {
          body,
          icon: '/images/icons/icon-192x192.png',
          badge: '/images/icons/badge-96x96.png',
        });
      }
      window.dispatchEvent(new Event('fcm-message'));
    });
    return () => unsubscribe();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
          <Toast />
          <Routes>
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />}
            />
            <Route path="/login" element={<Login />} />
            {COMMERCE_ENABLED && <Route path="/pricing" element={<Pricing />} />}
            <Route
              path="/dashboard"
              element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />}
            />
            <Route
              path="/route-map"
              element={isAuthenticated ? (
                <Suspense fallback={<div className="min-h-screen bg-snow p-8 text-center text-sm font-medium text-sub-text" role="status">노선 지도를 준비하고 있어요...</div>}>
                  <RouteMap />
                </Suspense>
              ) : <Navigate to="/login" />}
            />
            <Route
              path="/schedules"
              element={isAuthenticated ? <Schedules /> : <Navigate to="/login" />}
            />
            <Route
              path="/schedules/bulk"
              element={isAuthenticated ? <ScheduleBulkForm /> : <Navigate to="/login" />}
            />
            <Route
              path="/reserve"
              element={isAuthenticated ? <QuickReserve /> : <Navigate to="/login" />}
            />
            <Route
              path="/reservations"
              element={isAuthenticated ? <Reservations /> : <Navigate to="/login" />}
            />
            <Route
              path="/logs"
              element={isAuthenticated ? <Logs /> : <Navigate to="/login" />}
            />
            {REFERRAL_ENABLED && (
              <Route
                path="/referral"
                element={isAuthenticated ? <Referral /> : <Navigate to="/login" />}
              />
            )}
            <Route
              path="/r/:code"
              element={<ReferralRedirect />}
            />
            <Route element={<AdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/reserve" element={<AdminReserve />} />
                <Route path="/admin/logs" element={<AdminLogs />} />
                <Route path="/admin/schedules" element={<AdminSchedules />} />
                <Route path="/admin/taekyung" element={<AdminTaekyung />} />
                <Route path="/admin/notifications" element={<AdminNotifications />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
